import Phaser from 'phaser';
import { Component } from '../Component';

/**
 * SpriteComponent - Visual representation
 * Links an entity to its Phaser sprite
 */
export class SpriteComponent extends Component {
  public readonly type = 'sprite';

  /** Phaser sprite reference */
  public sprite: Phaser.GameObjects.Sprite | null;

  /** Texture key */
  public textureKey: string;

  /** Current animation key */
  public currentAnimation: string;

  /** Sprite scale */
  public scaleX: number;
  public scaleY: number;

  /** Tint color (0xffffff = no tint) */
  public tint: number;

  /** Alpha transparency */
  public alpha: number;

  /** Whether sprite is visible */
  public visible: boolean;

  /** Depth for sorting (calculated from position) */
  public depth: number;

  constructor(data?: Partial<SpriteComponent>) {
    super();
    this.sprite = data?.sprite ?? null;
    this.textureKey = data?.textureKey ?? '';
    this.currentAnimation = data?.currentAnimation ?? 'idle';
    this.scaleX = data?.scaleX ?? 1;
    this.scaleY = data?.scaleY ?? 1;
    this.tint = data?.tint ?? 0xffffff;
    this.alpha = data?.alpha ?? 1;
    this.visible = data?.visible ?? true;
    this.depth = data?.depth ?? 0;
  }

  /**
   * Play an animation
   */
  public playAnimation(key: string, ignoreIfPlaying = true): void {
    if (this.sprite && key !== this.currentAnimation) {
      this.sprite.play(key, ignoreIfPlaying);
      this.currentAnimation = key;
    }
  }

  /**
   * Set sprite visibility
   */
  public setVisible(visible: boolean): void {
    this.visible = visible;
    if (this.sprite) {
      this.sprite.setVisible(visible);
    }
  }

  /**
   * Set sprite tint
   */
  public setTint(tint: number): void {
    this.tint = tint;
    if (this.sprite) {
      this.sprite.setTint(tint);
    }
  }

  /**
   * Flash the sprite (damage effect, etc.)
   */
  public flash(color: number = 0xffffff, duration: number = 100): void {
    if (!this.sprite) return;

    const originalTint = this.tint;
    this.sprite.setTint(color);

    this.sprite.scene.time.delayedCall(duration, () => {
      if (this.sprite) {
        this.sprite.setTint(originalTint);
      }
    });
  }
}
